function escapeHtml(text) {
  return String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Precio unitario del email de oferta: explícito o total / unidades. */
export function resolveOfferUnitPrice(it = {}) {
  const explicit = Number(it.precioUnitario);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  const total = Number(it.totalVenta);
  const units = Number(it.units);
  if (Number.isFinite(total) && Number.isFinite(units) && units > 0) {
    return total / units;
  }
  return null;
}

function formatOfferMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatOfferUnits(value) {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("es-ES", { maximumFractionDigits: 0 });
}

function resolveCurrency(it = {}) {
  const c = String(it.moneda || it.currency || "")
    .trim()
    .toUpperCase();
  if (!c) return "EUR";
  if (c === "EURO" || c === "€") return "EUR";
  if (c === "$" || c === "DOLAR" || c === "DOLARES") return "USD";
  return c;
}

/**
 * Moneda de venta del escandallo (líneas). Presupuesto listado no trae MonedaVenta.
 */
export function resolveOfferCurrencyFromLines(lines = [], fallback = "EUR") {
  const fromLine = (Array.isArray(lines) ? lines : []).find((l) =>
    String(l?.MonedaVenta ?? l?.monedaVenta ?? "").trim(),
  );
  return resolveCurrency({
    moneda: fromLine?.MonedaVenta ?? fromLine?.monedaVenta ?? fallback,
  });
}

function offerNotesBlock(isEs, notas) {
  if (!String(notas || "").trim()) return "";
  return `<p style="margin:16px 0 0;font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#262c34;line-height:1.45"><strong style="color:#163a66">${escapeHtml(
    isEs ? "Notas" : "Notes",
  )}:</strong><br/>${escapeHtml(notas).replace(/\n/g, "<br/>")}</p>`;
}

/** Cierre corto: TON añade la firma de la cuenta From después. */
function offerClosingHtml(isEs) {
  const thanks = isEs
    ? "Quedamos a tu disposición.\n\nUn saludo,"
    : "Best regards,";
  return `<p style="margin:22px 0 0;font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#262c34;line-height:1.45">${escapeHtml(
    thanks,
  ).replace(/\n/g, "<br/>")}</p>`;
}

function thCell(text, align) {
  return `<th align="${align}" bgcolor="#1976D2" style="background:#1976d2;color:#ffffff;padding:11px 12px;font-size:11px;letter-spacing:0.05em;text-transform:uppercase;font-weight:700;font-family:Arial,Helvetica,sans-serif;text-align:${align};border:none">${escapeHtml(
    text,
  )}</th>`;
}

function tdCell(html, { bg, align, bold, accent } = {}) {
  const color = accent ? "#0d47a1" : "#262c34";
  const weight = bold ? "700" : "400";
  return `<td align="${align || "left"}" bgcolor="${bg}" style="background:${bg};padding:11px 12px;border-bottom:1px solid #e3eef8;font-family:Arial,Helvetica,sans-serif;font-size:13px;color:${color};font-weight:${weight};text-align:${
    align || "left"
  };vertical-align:middle">${html}</td>`;
}

/**
 * Tabla de oferta: cabecera azul, zebra, solo precio unitario (sin totales).
 * HTML email-safe (tablas, bgcolor, Arial) para Outlook / Gmail.
 */
export function buildOfferStylesTable({ isEs, items = [] } = {}) {
  const head = isEs
    ? ["Estilo", "Presupuesto", "Unidades", "Precio unitario", "Moneda"]
    : ["Style", "Estimate", "Units", "Unit price", "Currency"];
  const list = Array.isArray(items) ? items : [];

  const body = list
    .map((it, idx) => {
      const alt = idx % 2 === 1;
      const bg = alt ? "#F3F8FD" : "#FFFFFF";
      const style = escapeHtml(it.diseno || it.nombrePresupuesto || "—");
      const num = escapeHtml(it.numeroPresupuesto || "—");
      const unitsTxt = formatOfferUnits(it.units) || "—";
      const unit = resolveOfferUnitPrice(it);
      const unitTxt = unit != null ? formatOfferMoney(unit) : "—";
      const currency = resolveCurrency(it);
      return `<tr>
${tdCell(style, { bg, align: "left", bold: true })}
${tdCell(num, { bg, align: "left" })}
${tdCell(escapeHtml(unitsTxt), { bg, align: "right" })}
${tdCell(escapeHtml(unitTxt), { bg, align: "right", bold: true, accent: true })}
${tdCell(escapeHtml(currency), { bg, align: "center" })}
</tr>`;
    })
    .join("");

  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;margin:16px 0;max-width:720px;border:1px solid #bbdefb">
<thead>
<tr>
${thCell(head[0], "left")}
${thCell(head[1], "left")}
${thCell(head[2], "right")}
${thCell(head[3], "right")}
${thCell(head[4], "center")}
</tr>
</thead>
<tbody>
${body}
</tbody>
</table>`;
}

export function buildClientOfferBatchHtmlDraft({
  lang = "en",
  items = [],
  notas,
} = {}) {
  const isEs = lang === "es";
  const hello = isEs ? "Hola," : "Hello,";
  const intro = isEs
    ? "Adjuntamos nuestra oferta / presupuesto de Stage Design para los siguientes estilos."
    : "Please find attached our Stage Design quotation for the following styles.";
  return `
<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#262c34;line-height:1.45">
<p style="margin:0 0 8px">${escapeHtml(hello)}</p>
<p style="margin:0 0 4px">${escapeHtml(intro)}</p>
${buildOfferStylesTable({ isEs, items })}
${offerNotesBlock(isEs, notas)}
${offerClosingHtml(isEs)}
</div>
`.trim();
}

export function buildClientOfferHtmlDraft({
  lang = "en",
  numeroPresupuesto,
  nombrePresupuesto,
  units,
  temporada,
  diseno,
  notas,
  precioUnitario,
  totalVenta,
  moneda,
} = {}) {
  const isEs = lang === "es";
  const hello = isEs ? "Hola," : "Hello,";
  const intro = isEs
    ? "Adjuntamos nuestra oferta / presupuesto de Stage Design."
    : "Please find attached our Stage Design quotation.";
  const season = String(temporada || "").trim();
  const seasonLine = season
    ? `<p style="margin:0 0 4px;font-size:13px;color:#555"><strong>${escapeHtml(
        isEs ? "Temporada" : "Season",
      )}:</strong> ${escapeHtml(season)}</p>`
    : "";
  return `
<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#262c34;line-height:1.45">
<p style="margin:0 0 8px">${escapeHtml(hello)}</p>
<p style="margin:0 0 4px">${escapeHtml(intro)}</p>
${seasonLine}
${buildOfferStylesTable({
  isEs,
  items: [
    {
      diseno: diseno || nombrePresupuesto,
      nombrePresupuesto,
      numeroPresupuesto,
      units,
      precioUnitario,
      totalVenta,
      moneda,
    },
  ],
})}
${offerNotesBlock(isEs, notas)}
${offerClosingHtml(isEs)}
</div>
`.trim();
}
